import { useAuth, useOrganization } from '@clerk/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isWorkspaceKind,
  SEQUENCER_CONNECTION_KINDS,
} from '@/components/agent-chat/agent-cards.util';
import { serverReason } from '@/containers/agents/server-reason.util';
import { connectFailedMessage } from '@/containers/integrations/connect-error-copy.util';
import {
  connectInPopup,
  onConnectPopupReturn,
} from '@/containers/integrations/connect-popup.util';
import type { EngagementProvider } from '@/containers/integrations/sequencer-connect.component';
import { ownLinkedInAccounts } from '@/containers/playbooks/custom-builder/config/linkedin-action.util';
import { ownMailboxes } from '@/containers/playbooks/custom-builder/config/mailbox-email.util';
import { useSenderAccounts } from '@/containers/sequences/use-sender-accounts.hook';
import { useCrm } from '@/hooks/use-crm.hook';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { isCluHost } from '@/lib/brand';
import { rememberConnectingProvider } from '@/lib/workspace-oauth-callback';
import { useListSequencerConnectionsQuery } from '@/store/features/sequencers.feature';
import {
  useConnectSlackAgentMutation,
  useGetSlackAgentStatusQuery,
} from '@/store/features/slack.feature';
import { useGetTeamsStatusQuery } from '@/store/features/teams.feature';
import {
  useConnectWorkspaceMutation,
  useGetWorkspaceStatusQuery,
} from '@/store/features/workspace-connections.feature';

/** A sign-in window left open this long was abandoned. */
const ABANDON_MS = 5 * 60_000;

const CRM_KINDS: readonly string[] = ['salesforce', 'hubspot', 'attio'];
const ENGAGEMENT_KINDS: readonly string[] = [
  'salesloft',
  'outreach',
  'nooks',
  'instantly',
] satisfies EngagementProvider[];

/**
 * How Clu's card connects each app, with the flow its Integrations card
 * uses: the mailbox dialog for email, the sign-in dialog for LinkedIn, the key dialogs for the outside
 * sequencers (lemlist's own), Gong's choice of sign-in or key, and the
 * provider's sign-in in a window for the CRMs, Slack and the workspace
 * tools. Teams and an app the card doesn't know keep the Integrations link.
 */
export type InlineConnectFlow =
  | 'mailbox'
  | 'linkedin'
  | 'lemlist'
  | 'sequencer'
  | 'gong'
  | 'redirect'
  | 'link';

export function inlineConnectFlow(kind: string): InlineConnectFlow {
  if (kind === 'email') return 'mailbox';
  if (kind === 'linkedin') return 'linkedin';
  if (kind === 'lemlist') return 'lemlist';
  if (ENGAGEMENT_KINDS.includes(kind)) return 'sequencer';
  if (kind === 'gong') return 'gong';
  // The CRMs' and Slack's sign-ins return to app.cleanlist.ai, so on Clu's
  // domain a window could never report back: Integrations, as before.
  if (
    (CRM_KINDS.includes(kind) || kind === 'slack') &&
    isCluHost(window.location.hostname)
  )
    return 'link';
  if (CRM_KINDS.includes(kind) || kind === 'slack' || isWorkspaceKind(kind))
    return 'redirect';
  return 'link';
}

/** `waiting` while the sign-in window is open. */
export type InlineConnectPhase = 'idle' | 'starting' | 'waiting';

/**
 * One app's connection for Clu's card: read live from the status its
 * Integrations card reads, again when a sign-in window reports back and when
 * this tab comes back into focus, so the card turns to Connected on its own.
 * `start` opens the sign-in window for a redirect flow; call it from the
 * click, which the window needs.
 *
 * `afterConnect` is for an app a run just stopped on: the result's own
 * `connected` is ignored and the card turns to Connected only from a live
 * read once a connect here succeeded: a dialog's success (`arm`) or the
 * sign-in window reporting ok. Starting one, or a failed one, never counts.
 */
export function useInlineConnect(
  kind: string,
  name: string,
  {
    initiallyConnected,
    afterConnect = false,
  }: { initiallyConnected: boolean; afterConnect?: boolean }
) {
  const { organization } = useOrganization();
  const { userId } = useAuth();
  const orgId = organization?.id ?? '';
  const crm = CRM_KINDS.includes(kind);
  const workspace = isWorkspaceKind(kind) ? kind : null;
  const sequencer = SEQUENCER_CONNECTION_KINDS.includes(kind);

  const crmRead = useCrm(false, { skip: !crm });
  const workspaceRead = useGetWorkspaceStatusQuery(
    { provider: workspace ?? 'google_sheets', organizationId: orgId },
    { skip: !workspace || !orgId }
  );
  const slackRead = useGetSlackAgentStatusQuery(orgId, {
    skip: kind !== 'slack' || !orgId,
  });
  const sequencerRead = useListSequencerConnectionsQuery(
    { orgId },
    { skip: !sequencer || !orgId }
  );
  const teamsRead = useGetTeamsStatusQuery(orgId, {
    skip: kind !== 'teams' || !orgId,
  });
  const mailboxRead = useSenderAccounts({
    skip: kind !== 'email' && kind !== 'linkedin',
  });
  const { isAppSumoOnly } = useFeatureAccess();
  const mailboxes = mailboxRead.email;

  const live = crm
    ? crmRead.crmOptions?.find((option) => option.type === kind)
        ?.is_connected === true
    : workspace
      ? workspaceRead.currentData?.connected === true
      : kind === 'slack'
        ? slackRead.currentData?.connected === true
        : sequencer
          ? sequencerRead.currentData?.find((row) => row.provider === kind)
              ?.connected === true
          : kind === 'teams'
            ? teamsRead.currentData?.connected === true
            : kind === 'email'
              ? // The member's own mailbox: a teammate's can't send for them.
                ownMailboxes(mailboxes, userId, undefined).length > 0
              : kind === 'linkedin'
                ? ownLinkedInAccounts(mailboxRead.linkedin, userId).length > 0
                : false;
  const sequencerRow = sequencerRead.currentData?.find(
    (row) => row.provider === kind
  );
  // The server's list leaves out an app the org can't have (Nooks without
  // nooks_v1): no card, as Integrations shows none.
  const hidden = sequencer && !!sequencerRead.currentData && !sequencerRow;
  // What the Integrations card wouldn't offer to connect (a plan, an admin, a
  // setup it lacks) opens Integrations, which says why, instead.
  const offered = crm
    ? !crmRead.crmOptions ||
      !!crmRead.crmOptions.find((option) => option.type === kind)?.url
    : workspace
      ? !isAppSumoOnly
      : kind === 'slack'
        ? !slackRead.currentData ||
          slackRead.currentData.connected === true ||
          slackRead.currentData.canInstall === true
        : sequencer
          ? !sequencerRow || sequencerRow.connected || sequencerRow.connectable
          : true;
  const [armed, setArmed] = useState(false);
  const arm = useCallback(() => setArmed(true), []);
  const connected = afterConnect ? armed && live : initiallyConnected || live;

  const { refetchCrm } = crmRead;
  const { refetch: refetchWorkspace } = workspaceRead;
  const { refetch: refetchSlack } = slackRead;
  const { refetch: refetchSequencers } = sequencerRead;
  const { refetch: refetchTeams } = teamsRead;
  const { refetch: refetchMailboxes } = mailboxRead;
  /** Reads this app's connection again (a read not started yet is skipped). */
  const refetch = useCallback(() => {
    const again = crm
      ? refetchCrm
      : workspace
        ? refetchWorkspace
        : kind === 'slack'
          ? refetchSlack
          : sequencer
            ? refetchSequencers
            : kind === 'teams'
              ? refetchTeams
              : kind === 'email' || kind === 'linkedin'
                ? refetchMailboxes
                : null;
    try {
      void again?.();
    } catch {
      // Skipped (no organization yet): nothing to read again.
    }
  }, [
    crm,
    workspace,
    sequencer,
    kind,
    refetchCrm,
    refetchWorkspace,
    refetchSlack,
    refetchSequencers,
    refetchTeams,
    refetchMailboxes,
  ]);

  const [phase, setPhase] = useState<InlineConnectPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const attempt = useRef(0);

  // The window's word, or this tab back in focus: read the connection again.
  useEffect(() => {
    if (connected) return;
    const back = () => refetch();
    window.addEventListener('focus', back);
    const stop = onConnectPopupReturn((message) => {
      if (message.kind !== kind) return;
      if (message.ok) setArmed(true);
      refetch();
      setPhase((current) => (current === 'waiting' ? 'idle' : current));
      if (!message.ok)
        setError(
          message.error
            ? connectFailedMessage(kind, message.error)
            : 'The connection did not finish.'
        );
    });
    return () => {
      window.removeEventListener('focus', back);
      stop();
    };
  }, [connected, kind, refetch]);

  // Provider pages can sever the opener, so a closed window can't be seen.
  // Only the window's word or the abandon timer ends the wait.
  useEffect(() => {
    if (phase !== 'waiting') return;
    const abandon = window.setTimeout(() => setPhase('idle'), ABANDON_MS);
    return () => window.clearTimeout(abandon);
  }, [phase]);

  const [connectWorkspace] = useConnectWorkspaceMutation();
  const [connectSlack] = useConnectSlackAgentMutation();
  const authorizeUrl = useCallback(async (): Promise<string> => {
    if (crm) {
      const urlIn = (options?: readonly { type: string; url?: string }[]) =>
        options?.find((option) => option.type === kind)?.url;
      // Options not loaded yet: read them now (the window is already open).
      const url =
        urlIn(crmRead.crmOptions) ?? urlIn(await refetchCrm().unwrap());
      if (!url) throw new Error('unavailable');
      return url;
    }
    if (workspace)
      return (
        await connectWorkspace({
          provider: workspace,
          organizationId: orgId,
        }).unwrap()
      ).authorizeUrl;
    return (await connectSlack(orgId).unwrap()).authorizeUrl;
  }, [
    crm,
    kind,
    crmRead.crmOptions,
    refetchCrm,
    workspace,
    connectWorkspace,
    orgId,
    connectSlack,
  ]);

  const start = useCallback(() => {
    if (!orgId) return;
    const mine = ++attempt.current;
    setError(null);
    setPhase('starting');
    // Google's products share one callback page, which reads the product
    // from sessionStorage: stored before the window opens, so it has a copy.
    if (workspace) rememberConnectingProvider(workspace);
    connectInPopup(kind, authorizeUrl).then(
      (how) => {
        if (mine === attempt.current)
          setPhase(how === 'popup' ? 'waiting' : 'idle');
      },
      (refused: unknown) => {
        if (mine !== attempt.current) return;
        setPhase('idle');
        setError(
          serverReason(refused, `Couldn’t start the ${name} connection.`)
        );
      }
    );
  }, [orgId, workspace, kind, authorizeUrl, name]);

  return {
    orgId,
    connected,
    hidden,
    flow: offered ? inlineConnectFlow(kind) : ('link' as const),
    phase,
    error,
    start,
    arm,
    refetch,
    /** Email only: the org's email mailboxes, as the sender pickers read them. */
    mailboxes,
    /** Email only: false until the mailbox list has answered, or failed. */
    mailboxesLoaded: mailboxRead.isSuccess || mailboxRead.isError,
    /**
     * LinkedIn only: the member's own disconnected account, so a reconnect
     * lands on the same row (and its sequences) instead of a new one.
     */
    linkedinReconnect:
      kind === 'linkedin' && userId
        ? (mailboxRead.linkedin.find(
            (account) => account.owner_user_id === userId && !account.connected
          ) ?? null)
        : null,
    /**
     * LinkedIn only: true once we know who the member is and their accounts
     * have loaded, so a reconnect can't open before its account is known.
     */
    linkedinReady: kind !== 'linkedin' || (!!userId && mailboxRead.isSuccess),
  };
}
